import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, ChevronRight, Loader, Calendar, Phone } from 'lucide-react';
import api from '../../utils/api';
import { getDishDisplayName } from '../../utils/dishDisplay';
import { DEFAULT_AVATAR } from '../../utils/constants';
import './ContributorList.scss';

const ContributorList = () => {
    const navigate = useNavigate();
    const [contributors, setContributors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [year, setYear] = useState(2026);

    useEffect(() => {
        fetchContributors();
    }, [year]);

    const fetchContributors = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/contributors', { params: { year } });
            if (response.data.success) {
                setContributors(response.data.contributors || []);
            }
        } catch (err) {
            console.error('Error fetching contributors:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredContributors = contributors.filter(c => {
        const term = search.toLowerCase();
        return (
            (c.name || '').toLowerCase().includes(term) ||
            (c.email || '').toLowerCase().includes(term) ||
            (c.phone || '').includes(search)
        );
    });

    const handleContributorClick = (contributor) => {
        navigate(`/contributors/${encodeURIComponent(contributor.email)}`);
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    return (
        <div className="contributor-list">
            <div className="page-header">
                <h2>Contributors</h2>
                <p className="page-subtitle">
                    {contributors.length} contributor{contributors.length !== 1 ? 's' : ''} total
                </p>
            </div>

            <div className="filters">
                <div className="search-box">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search by name, email or phone..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className="year-filter"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                >
                    <option value={2025}>2025</option>
                    <option value={2026}>2026</option>
                    <option value={2027}>2027</option>
                </select>
            </div>

            {loading ? (
                <div className="loading-state">
                    <Loader size={32} className="spinner" />
                    <p>Loading contributors...</p>
                </div>
            ) : (
                <div className="contributors-table">
                    {filteredContributors.length === 0 ? (
                        <div className="empty-state">
                            <User size={48} />
                            <p>No contributors found</p>
                        </div>
                    ) : (
                        filteredContributors.map((contributor) => (
                            <div
                                key={contributor.email}
                                className="contributor-row"
                                onClick={() => handleContributorClick(contributor)}
                            >
                                <div className="contributor-avatar-wrap">
                                    <img
                                        src={contributor.picture || DEFAULT_AVATAR}
                                        alt={contributor.name}
                                        className="contributor-avatar"
                                        onError={(e) => {
                                            e.target.src = DEFAULT_AVATAR;
                                        }}
                                    />
                                </div>
                                <div className="contributor-info">
                                    <span className="contributor-name">{contributor.name || 'Unknown'}</span>
                                    <span className="contributor-email">{contributor.email}</span>
                                    {contributor.phone && (
                                        <a href={`tel:${contributor.phone}`} className="contributor-phone" onClick={(e) => e.stopPropagation()}>
                                            <Phone size={14} />
                                            {contributor.phone}
                                        </a>
                                    )}
                                </div>
                                <div className="contributor-stats">
                                    <span className="stat-badge" title="Total mosques">
                                        🕌 {contributor.totalMosques} mosque{contributor.totalMosques !== 1 ? 's' : ''}
                                    </span>
                                    {contributor.verifiedMosques > 0 && (
                                        <span className="stat-verified">
                                            {contributor.verifiedMosques} verified
                                        </span>
                                    )}
                                    <span className="stat-visits" title="Total views">
                                        👁️ {contributor.totalVisits ?? 0}
                                    </span>
                                    <span className="stat-directions" title="Directions clicked">
                                        📤 {contributor.totalDirectionClicks ?? 0}
                                    </span>
                                </div>
                                <div className="contributor-dates">
                                    <span className="date-label">First:</span> {formatDate(contributor.firstContribution)}
                                    <br />
                                    <span className="date-label">Last:</span> {formatDate(contributor.lastContribution)}
                                </div>
                                <ChevronRight size={20} className="chevron" />
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default ContributorList;
